import { useIdle } from "react-use";
import { useAccount, useBalance, useReadContracts } from "wagmi";

import { toFixedWithoutRounding } from "../constants/common";

import { stableVaultContractConfig } from "@config/contractsConfig";
import { formatUnits } from "viem";
import useDappChain from "./useDappChain";
import useDappConfig from "./useDappConfig";

const useTokenBalance = () => {
  const isIdle = useIdle(150e3);
  const { address: walletAddress } = useAccount();

  const {
    config,
    vaultsContractConfig,
    yieldVaultContractConfig,
    cryptaniumVaultContractConfig,
  } = useDappConfig();
  const { chain } = useDappChain();
  const tokens = config.tokens;
  const tokenKeys = Object.keys(tokens);

  // Get stable vault underlying tokens (USDC, USDT, etc.)
  const stableVaultTokens = stableVaultContractConfig[chain.id]?.tokens || {};
  const stableVaultTokenKeys = Object.keys(stableVaultTokens);

  // Get cryptanium vault underlying tokens (USDC, USDT, etc.)
  const cryptaniumVaultTokens =
    cryptaniumVaultContractConfig[chain.id]?.tokens || {};
  const cryptaniumVaultTokenKeys = Object.keys(cryptaniumVaultTokens);

  const balancesForTokens = [
    ...tokenKeys.map((token) => tokens[token]),
    ...stableVaultTokenKeys.map((token) => stableVaultTokens[token]),
    ...cryptaniumVaultTokenKeys.map((token) => cryptaniumVaultTokens[token]),
    config.xtoken,
    { ...(vaultsContractConfig[chain.id]?.xToken || {}) },
    { ...(yieldVaultContractConfig[chain.id]?.xToken || {}) },
    { ...(stableVaultContractConfig[chain.id]?.xToken || {}) },
    { ...(cryptaniumVaultContractConfig[chain.id]?.xToken || {}) },
  ];

  const filteredBalancesForTokens = balancesForTokens.filter(
    (token) => Object.keys(token).length > 0
  );

  const { data: tokensBalances } = useReadContracts({
    contracts: filteredBalancesForTokens.map((token) => ({
      address: token.address,
      abi: token.abi,
      functionName: "balanceOf",
      args: [walletAddress],
      chain,
    })),
    query: {
      enabled: !isIdle && !!walletAddress,
      refetchInterval: 5_000,
    },
  });

  const { data: nativeTokenBalance } = useBalance({
    address: walletAddress,
    query: {
      enabled: !isIdle && !!walletAddress,
      refetchInterval: 5_000,
      initialData: {
        decimals: config.nativeToken.decimals,
        formatted: "0",
        symbol: config.nativeToken.symbol,
        value: 0n,
      },
    },
  });

  const supportedTokenBalances: { [x: string]: number } = tokensBalances
    ? tokensBalances
        .map((res) => res.result! as bigint)
        .reduce((acc, curr, index) => {
          return {
            ...acc,
            [filteredBalancesForTokens[index].symbol]: +toFixedWithoutRounding(
              curr
                ? formatUnits(curr, filteredBalancesForTokens[index].decimals)
                : "0",
              8
            ),
          };
        }, {})
    : {};

  return {
    tokensBalances: tokensBalances ? supportedTokenBalances : {},
    nativeTokenBalance: +toFixedWithoutRounding(
      nativeTokenBalance?.formatted || "0",
      8
    ),
    xTokenTokenBalance: supportedTokenBalances[config.xtoken.symbol],
    xTokenVaultBalance:
      supportedTokenBalances[vaultsContractConfig[chain.id]?.xToken?.symbol],
    xTokenYieldVaultBalance:
      supportedTokenBalances[
        yieldVaultContractConfig[chain.id]?.xToken?.symbol
      ],
    xTokenStableVaultBalance:
      supportedTokenBalances[
        stableVaultContractConfig[chain.id]?.xToken?.symbol
      ],
    xTokenCryptaniumVaultBalance:
      supportedTokenBalances[
        cryptaniumVaultContractConfig[chain.id]?.xToken?.symbol
      ],
  };
};

export default useTokenBalance;
